import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { auth } from "@/firebaseConfig"; // your Firebase Auth instance
import type { User } from "firebase/auth";
import store from "@/stores";
import { isSessionExpired } from "@/stores/modules/auth";

const routes: RouteRecordRaw[] = [
  // Public
  {
    path: "/",
    name: "home",
    component: () => import("@/views/HomeView.vue"),
    meta: { title: "Home", requiresAuth: true },
  },
  {
    path: "/p/:id([A-Za-z0-9_-]{8,})",
    name: "event-public",
    props: true,
    meta: { hideMenu: true },
    component: () => import("@/views/events/EventViewPublic.vue"),
  },
  {
    path: "/song/:id/:key",
    name: "song",
    props: true,
    component: () => import("@/views/songs/SongView.vue"),
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/LoginView.vue"),
    meta: { guestOnly: true },
  },
  {
    path: "/translation",
    name: "translation",
    component: () => import("@/views/TranslationView.vue"),
    meta: { title: "Translation", hideHeader: true },
  },
  // Linked from the App Store listing, so they must work logged out.
  {
    path: "/privacy",
    name: "privacy",
    component: () => import("@/views/legal/PrivacyView.vue"),
    meta: { title: "Privacy policy", hideMenu: true },
  },
  {
    path: "/support",
    name: "support",
    component: () => import("@/views/legal/SupportView.vue"),
    meta: { title: "Support", hideMenu: true },
  },

  // Protected (require auth)
  {
    path: "/event/:id/:category?",
    name: "event",
    props: true,
    component: () => import("@/views/events/EventView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/event/:id/schedule/:scheduleId/song/:songId",
    name: "event-song",
    props: true,
    meta: { hideHeader: true, requiresAuth: true },
    component: () => import("@/views/events/EventSongView.vue"),
  },
  {
    path: "/event/:id/schedule/:scheduleId/sermon/:sermonId",
    name: "event-sermon",
    props: true,
    meta: { hideMenu: true, requiresAuth: true },
    component: () => import("@/views/events/EventSermonView.vue"),
  },
  {
    path: "/repertoire",
    name: "repertoire",
    component: () => import("@/views/songs/RepertoireView.vue"),
    meta: { title: "Repertoire", requiresAuth: true },
  },
  {
    path: "/repertoire/song/:id",
    name: "repertoire-song",
    props: true,
    component: () => import("@/views/songs/SongEditorView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/users",
    name: "users",
    component: () => import("@/views/users/UsersView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/teams",
    name: "teams",
    component: () => import("@/views/users/TeamsView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/profile",
    name: "profile",
    component: () => import("@/views/users/ProfileView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/organisation",
    name: "organisation",
    component: () => import("@/views/OrganisationSettingsView.vue"),
    meta: { title: "Organisation", requiresAuth: true },
  },
  {
    path: "/organisation/templates",
    name: "schedule-templates",
    component: () => import("@/views/ScheduleTemplatesView.vue"),
    meta: { title: "Schedule templates", requiresAuth: true },
  },
  {
    path: "/platform",
    name: "platform",
    component: () => import("@/views/PlatformOrganisationsView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/settings",
    name: "settings",
    component: () => import("@/views/SettingsView.vue"),
    meta: { requiresAuth: true },
  },

  // 404
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("@/views/NotFoundView.vue"),
    meta: { hideMenu: true },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, saved) {
    if (saved) return saved;
    if (to.hash) return { el: to.hash, behavior: "smooth" };
    // Turning the repertoire's page only changes the query; stay in place.
    if (to.name === "repertoire" && from.name === "repertoire") return false;
    return { top: 0 };
  },
  strict: false,
});

router.beforeEach(async (to) => {
  await store.dispatch("auth/initAuthListenerOnce");

  const needsAuth = to.matched.some((r) => r.meta?.requiresAuth);
  const guestOnly = to.matched.some((r) => r.meta?.guestOnly);

  const firebaseUser = auth.currentUser;
  const isLoggedIn = store.getters["auth/isLoggedIn"];

  if (firebaseUser && isSessionExpired(firebaseUser)) {
    await store.dispatch("auth/logOut");
    return {
      name: "login",
      query: { redirect: to.fullPath },
      replace: true,
    };
  }
  if (needsAuth && !isLoggedIn) {
    return { name: "login", query: { redirect: to.fullPath }, replace: true };
  }
  if (guestOnly && isLoggedIn) {
    return { path: (to.query.redirect as string) || "/", replace: true };
  }

  if (to.meta?.title) document.title = String(to.meta.title);
  return true;
});

export default router;
